import { type ProviderOffer } from "../../../../../types/dtos/api/sales/provider-offer.dto";
import { isFiniteNonNegativeNumber } from "../helpers/OfferPricingNumber.helper";
import { isNonEmptyText, resolveBookingUrlFromBookingUrls } from "../helpers/OfferPricingUrl.helper";
import { type OfferPricingEvaluationContext, type ProviderPricingEvaluation } from "../OfferPricing.types";
import { BaseOfferPricingProvider } from "../OfferPricingProvider.contract";

/**
 * Returns the rental provider name from the provider-offer payload when available.
 *
 * @param providerOffer Rental MOTIS provider offer payload.
 * @returns Rental provider display name from offer properties, or `null`.
 */
const getRentalProviderNameFromOffer = ( providerOffer: unknown ): string | null => {
    const providerOfferRecord = asRecord( providerOffer ) ?? {};
    const propertiesRecord = asRecord( providerOfferRecord["properties"] ) ?? {};
    const providerMetadataRecord = asRecord( propertiesRecord["provider"] ) ?? {};
    const providerName = providerMetadataRecord["name"];
    if ( !isNonEmptyText( providerName ) ) {
        return null;
    }

    return providerName;
};

/**
 * Returns the rental booking URL from provider-offer rental URI metadata.
 *
 * @param providerOffer Rental MOTIS provider offer payload.
 * @returns Booking URL when a valid URI exists, otherwise `null`.
 */
const getRentalBookingUrlFromOffer = ( providerOffer: unknown ): string | null => {
    const providerOfferRecord = asRecord( providerOffer ) ?? {};
    const propertiesRecord = asRecord( providerOfferRecord["properties"] ) ?? {};
    const urisRecord = asRecord( propertiesRecord["uris"] );
    if ( urisRecord === null ) {
        return null;
    }

    const normalizedUris: Record<string, string> = {};
    const defaultUri = urisRecord["default"];
    if ( isNonEmptyText( defaultUri ) ) {
        normalizedUris.default = defaultUri;
    }
    const iOSUri = urisRecord["iOS"];
    if ( isNonEmptyText( iOSUri ) ) {
        normalizedUris.iOS = iOSUri;
    }
    const androidUri = urisRecord["android"];
    if ( isNonEmptyText( androidUri ) ) {
        normalizedUris.android = androidUri;
    }

    if ( Object.keys( normalizedUris ).length === 0 ) {
        return null;
    }

    return resolveBookingUrlFromBookingUrls( normalizedUris );
};

/**
 * Safely narrows unknown payloads to plain object records.
 *
 * @param value Value to narrow.
 * @returns Plain object record, or `null` when the value is not an object.
 */
const asRecord = ( value: unknown ): Record<string, unknown> | null => {
    if ( typeof value !== "object" || value === null ) {
        return null;
    }

    return value as Record<string, unknown>;
};

/**
 * Extracts a finite non-negative rental price from an unknown provider payload.
 *
 * @param providerOffer Rental MOTIS provider offer payload.
 * @returns Normalized price or `null`.
 */
const getRentalPriceFromOffer = ( providerOffer: unknown ): number | null => {
    const providerOfferRecord = asRecord( providerOffer );
    const priceValue = providerOfferRecord?.["price"];

    return isFiniteNonNegativeNumber( priceValue ) ? priceValue : null;
};

export class RentalMotisOfferPricingProvider extends BaseOfferPricingProvider {

    /**
     * Creates the rental MOTIS pricing provider plugin.
     */
    public constructor() {
        super( "rental_motis" );
    }

    /**
     * Normalizes rental MOTIS price and booking URL values.
     *
     * @param _ Segment context (unused for this provider).
     * @param providerOffer Raw provider payload.
     * @returns Normalized provider evaluation.
     */
    public evaluate( _: OfferPricingEvaluationContext, providerOffer: ProviderOffer ): ProviderPricingEvaluation {
        const price = getRentalPriceFromOffer( providerOffer );
        const bookingUrl = getRentalBookingUrlFromOffer( providerOffer );
        const rentalProviderName = getRentalProviderNameFromOffer( providerOffer );

        return {
            "providerKey":  this.providerKey,
            "providerName": rentalProviderName ?? this.getProviderLabel(),
            price,
            bookingUrl,
        };
    }

}

export const rentalMotisOfferPricingProviderPlugin = new RentalMotisOfferPricingProvider();
